import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
	Box,
	Button,
	Checkbox,
	Grid2,
	IconButton,
	InputAdornment,
	LinearProgress,
	TextField,
	Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { useAnimatedRowReorder } from "../../../../hooks/useAnimatedRowReorder";
import { useEmailAddresses } from "../../emailAddresses/EmailAddressesPage";
import {
	RECIPIENT_FIELDS,
	type RecipientField,
} from "../models/FormInitialValues";
import { upsertEmailAddress } from "../repositories/EmailTopicsRepository";

const isValidEmail = (email: string): boolean => {
	if (!email.trim()) return true;
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email.trim());
};

type Props = {
	selectedByField: Record<RecipientField, number[]>;
	onToggle: (
		field: RecipientField,
		emailAddressId: number,
		checked: boolean,
	) => void;
	onError: (message: string) => void;
	onPendingAddressChange?: (pendingAddress: string | null) => void;
	includeSystem: boolean;
	disabled?: boolean;
	enabled?: boolean;
};

export const RecipientsTable = ({
	selectedByField,
	onToggle,
	onError,
	onPendingAddressChange,
	includeSystem,
	disabled = false,
	enabled = true,
}: Props) => {
	const queryClient = useQueryClient();

	const [addressFilter, setAddressFilter] = useState("");
	const [isAddingAddress, setIsAddingAddress] = useState(false);
	const [newAddress, setNewAddress] = useState("");
	const [newAddressId, setNewAddressId] = useState<number | null>(null);

	const {
		data: addressesData,
		isLoading: addressesLoading,
		error: addressesError,
	} = useEmailAddresses({ includeSystem, enabled });

	const selectedByFieldRef = useRef(selectedByField);
	selectedByFieldRef.current = selectedByField;

	const { containerRef, setRowRef, captureLayout, scrollRowIntoView } =
		useAnimatedRowReorder<number>();

	const getVisibleAddresses = () => {
		const isAddressSelected = (emailAddressId: number) =>
			RECIPIENT_FIELDS.some((recipient) =>
				selectedByFieldRef.current[recipient.field].includes(emailAddressId),
			);

		const all = addressesData ?? [];
		const needle = addressFilter.trim().toLowerCase();
		const filtered = needle
			? all.filter((address) => address.Address.toLowerCase().includes(needle))
			: all;

		return [...filtered].sort((a, b) => {
			const byChecked =
				Number(isAddressSelected(b.EmailAddressId)) -
				Number(isAddressSelected(a.EmailAddressId));

			return byChecked !== 0 ? byChecked : a.Address.localeCompare(b.Address);
		});
	};

	const visibleAddresses = getVisibleAddresses();

	const centerRowWhenRendered = (emailAddressId: number, attemptsLeft = 5) => {
		requestAnimationFrame(() => {
			if (!scrollRowIntoView(emailAddressId) && attemptsLeft > 0) {
				centerRowWhenRendered(emailAddressId, attemptsLeft - 1);
			}
		});
	};

	const saveAddressMutation = useMutation({
		mutationFn: (payload: { EmailAddressId: number | null; Address: string }) =>
			upsertEmailAddress(payload),
		onSuccess: async (emailAddressId) => {
			await queryClient.invalidateQueries({ queryKey: ["emailAddresses"] });
			setIsAddingAddress(false);
			setNewAddress("");
			setNewAddressId(emailAddressId ?? null);

			if (emailAddressId !== null) {
				centerRowWhenRendered(emailAddressId);
			}
		},
		onError: (addressError: Error) => {
			onError(addressError.message);
		},
	});

	const hasInvalidNewAddress =
		newAddress.trim() !== "" && !isValidEmail(newAddress);
	const canSaveNewAddress = newAddress.trim() !== "" && !hasInvalidNewAddress;

	const submitNewAddress = () => {
		if (!canSaveNewAddress) {
			return;
		}

		saveAddressMutation.mutate({ EmailAddressId: null, Address: newAddress });
	};

	const selectRecipientType = (
		field: RecipientField,
		emailAddressId: number,
		checked: boolean,
	) => {
		captureLayout();

		if (checked) {
			for (const recipient of RECIPIENT_FIELDS) {
				if (
					recipient.field !== field &&
					selectedByField[recipient.field].includes(emailAddressId)
				) {
					onToggle(recipient.field, emailAddressId, false);
				}
			}
		}

		onToggle(field, emailAddressId, checked);
	};

	const toggleAllVisible = (field: RecipientField, checked: boolean) => {
		captureLayout();

		const selected = selectedByField[field];

		for (const address of visibleAddresses) {
			const isInField = selected.includes(address.EmailAddressId);

			if (checked && !isInField) {
				selectRecipientType(field, address.EmailAddressId, true);
			} else if (!checked && isInField) {
				onToggle(field, address.EmailAddressId, false);
			}
		}
	};

	const isAddressBusy = saveAddressMutation.isPending;

	useEffect(() => {
		if (onPendingAddressChange)
			onPendingAddressChange(
				isAddingAddress && canSaveNewAddress ? newAddress.trim() : null,
			);
	}, [isAddingAddress, canSaveNewAddress, newAddress]);

	useEffect(() => {
		if (newAddressId === null) {
			return;
		}

		const clearHighlight = () => setNewAddressId(null);

		window.addEventListener("mousemove", clearHighlight, { once: true });
		window.addEventListener("pointerdown", clearHighlight, { once: true });

		return () => {
			window.removeEventListener("mousemove", clearHighlight);
			window.removeEventListener("pointerdown", clearHighlight);
		};
	}, [newAddressId]);

	useEffect(() => {
		if (addressesError) {
			onError(addressesError.message);
		}
	}, [addressesError, onError]);

	return (
		<>
			<Typography variant="subtitle2" fontWeight={600}>
				Recipients
			</Typography>

			<TextField
				fullWidth
				label="Search address"
				variant="outlined"
				size="small"
				margin="dense"
				value={addressFilter}
				disabled={disabled}
				onChange={(event) => setAddressFilter(event.target.value)}
				sx={{ mb: 1 }}
				slotProps={{
					input: {
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon />
							</InputAdornment>
						),
					} as any,
				}}
			/>

			{addressesLoading ? <LinearProgress /> : null}

			<Box
				sx={{
					border: 1,
					borderColor: "divider",
					borderRadius: 1,
					overflow: "hidden",
				}}
			>
				<Box ref={containerRef} sx={{ maxHeight: "30vh", overflowY: "auto" }}>
					<Grid2
						container
						alignItems="center"
						sx={{
							position: "sticky",
							top: 0,
							zIndex: 2,
							backgroundColor: "background.paper",
							borderBottom: 1,
							borderColor: "divider",
							px: 1,
							py: 0.5,
						}}
					>
						<Grid2 size={{ xs: 5 }}>
							<Typography variant="caption" fontWeight={600}>
								Address
							</Typography>
						</Grid2>
						{RECIPIENT_FIELDS.map((recipient) => {
							const selected = selectedByField[recipient.field];
							const selectedVisible = visibleAddresses.filter((address) =>
								selected.includes(address.EmailAddressId),
							).length;
							const allSelected =
								visibleAddresses.length > 0 &&
								selectedVisible === visibleAddresses.length;

							return (
								<Grid2
									key={recipient.field}
									size={{ xs: 2 }}
									sx={{
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
									}}
								>
									<Typography variant="caption" fontWeight={600}>
										{recipient.label}
									</Typography>
									<Checkbox
										size="small"
										color="primary"
										sx={{ p: 0.25 }}
										title={`Select all ${recipient.label}`}
										disabled={disabled || visibleAddresses.length === 0}
										checked={allSelected}
										indeterminate={selectedVisible > 0 && !allSelected}
										onChange={(event) =>
											toggleAllVisible(recipient.field, event.target.checked)
										}
									/>
								</Grid2>
							);
						})}
						<Grid2 size={{ xs: 1 }} />
					</Grid2>

					{visibleAddresses.map((address) => {
						const isHighlighted = newAddressId === address.EmailAddressId;

						return (
							<Grid2
								container
								alignItems="center"
								key={address.EmailAddressId}
								ref={setRowRef(address.EmailAddressId)}
								sx={{
									px: 1,
									minHeight: 38,
									transition: "background-color 200ms ease",
									backgroundColor: isHighlighted
										? "action.hover"
										: "background.paper",
									...(newAddressId === null
										? { "&:hover": { backgroundColor: "action.hover" } }
										: {}),
								}}
							>
								<Grid2 size={{ xs: 5 }}>
									<Typography variant="body2" noWrap>
										{address.Address}
									</Typography>
								</Grid2>
								{RECIPIENT_FIELDS.map((recipient) => (
									<Grid2
										key={recipient.field}
										size={{ xs: 2 }}
										sx={{ display: "flex", justifyContent: "center" }}
									>
										<Checkbox
											size="small"
											color="primary"
											disabled={disabled}
											checked={selectedByField[recipient.field].includes(
												address.EmailAddressId,
											)}
											onChange={(event) =>
												selectRecipientType(
													recipient.field,
													address.EmailAddressId,
													event.target.checked,
												)
											}
										/>
									</Grid2>
								))}
							</Grid2>
						);
					})}

					{!addressesLoading && visibleAddresses.length === 0 ? (
						<Typography
							variant="body2"
							sx={{ px: 1, py: 2, textAlign: "center" }}
						>
							{addressFilter.trim()
								? "No addresses match the search."
								: "No addresses in the catalog."}
						</Typography>
					) : null}
				</Box>

				<Box
					sx={{
						borderTop: 1,
						borderColor: "divider",
						backgroundColor: "background.paper",
						px: 1,
						py: 0.5,
					}}
				>
					{isAddingAddress ? (
						<Grid2 container alignItems="center" spacing={1}>
							<Grid2 size={{ xs: 9 }}>
								<TextField
									fullWidth
									autoFocus
									variant="standard"
									placeholder="name@company.com"
									value={newAddress}
									disabled={isAddressBusy}
									error={hasInvalidNewAddress}
									helperText={hasInvalidNewAddress ? "Invalid" : undefined}
									onChange={(event) => setNewAddress(event.target.value)}
									onKeyDown={(event) => {
										if (event.key === "Enter") {
											event.preventDefault();
											event.stopPropagation();

											if (!isAddressBusy && canSaveNewAddress) {
												submitNewAddress();
											}

											return;
										}

										if (event.key === "Escape") {
											event.preventDefault();
											event.stopPropagation();
											setIsAddingAddress(false);
											setNewAddress("");
										}
									}}
								/>
							</Grid2>
							<Grid2
								size={{ xs: 3 }}
								sx={{ display: "flex", justifyContent: "flex-end" }}
							>
								<IconButton
									type="button"
									size="small"
									title="Save"
									disabled={isAddressBusy || !canSaveNewAddress}
									onClick={submitNewAddress}
								>
									<CheckIcon fontSize="small" color="primary" />
								</IconButton>
								<IconButton
									type="button"
									size="small"
									title="Cancel"
									disabled={isAddressBusy}
									onClick={() => {
										setIsAddingAddress(false);
										setNewAddress("");
									}}
								>
									<CloseIcon fontSize="small" />
								</IconButton>
							</Grid2>
						</Grid2>
					) : (
						<Button
							type="button"
							size="small"
							startIcon={<AddIcon />}
							disabled={isAddressBusy}
							onClick={() => setIsAddingAddress(true)}
						>
							Add new address
						</Button>
					)}
				</Box>
			</Box>
		</>
	);
};
